<script setup lang="ts">
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';

export type StepperStep = {
    title: string;
    mobileTitle: string;
    icon: IconDefinition;
    mobileIcon?: IconDefinition;
    clickDisabled?: boolean;
    url: string | null;
    pageHeaderTitle?: string;
};

type StepperStepProp = {
    title: string;
    mobileTitle: string;
    icon: {
        icon: IconDefinition;
        color: string;
        titleColor: string;
    };
    mobileIcon?: {
        icon?: IconDefinition;
        color: string;
        titleColor: string;
    };
    clickDisabled?: boolean;
    state: 'current' | 'next' | 'prev' | undefined;
};
interface IProps {
    steps?: StepperStep[];
    disabled?: boolean;
}
const { steps = [], disabled } = defineProps<IProps>();

const modelValue = defineModel<number>({ default: 0 });

const stepState = (index: number) => {
    switch (true) {
        case index === modelValue.value:
            return 'current';
        case index > modelValue.value:
            return 'next';
        case index < modelValue.value:
            return 'prev';
        default:
            break;
    }
};

const actualSteps = computed<StepperStepProp[]>(() =>
    steps?.map((step, index) => {
        const state = stepState(index);
        return {
            ...step,
            clickDisabled: step.clickDisabled || disabled,
            icon: {
                icon: step.icon,
                color:
                    state === 'prev'
                        ? 'var(--blue-400)'
                        : state === 'current'
                          ? 'white'
                          : 'var(--blue-100)',
                titleColor:
                    state === 'prev'
                        ? 'white'
                        : state === 'current'
                          ? 'white'
                          : 'white',
            },
            mobileIcon: {
                icon: step.mobileIcon,
                color:
                    state === 'prev'
                        ? 'var(--green-400)'
                        : state === 'current'
                          ? 'var(--blue-400)'
                          : 'var(--cool-gray-700)',
                titleColor:
                    state === 'prev'
                        ? 'var(--green-400)'
                        : state === 'current'
                          ? 'var(--blue-400)'
                          : 'var(--cool-gray-700)',
            },
            state,
        };
    })
);
const click = (index: number) => {
    if (actualSteps.value[index].clickDisabled) {
        return;
    }
    if (modelValue.value <= index) {
        return;
    }
    modelValue.value = index;
};
</script>
<template>
    <div class="mr-stepper-container desktop-only">
        <div class="mr-stepper-root">
            <template
                v-for="(step, index) in actualSteps"
                :key="`content-${step.title}`"
            >
                <div
                    class="step-content"
                    :class="{
                        clickable: !step.clickDisabled && index < modelValue,
                        disabled: step.clickDisabled && index < modelValue,
                    }"
                    @click.stop="click(index)"
                >
                    <div :class="`icon-wrapper ${step.state}`">
                        <mr-icon
                            :icon="step.icon.icon"
                            :color="step.icon.color"
                            size="small"
                        />
                    </div>
                    <mr-typography
                        white-space="nowrap"
                        :color="step.icon.titleColor"
                    >
                        {{ step.title }}
                    </mr-typography>
                </div>
                <mr-divider
                    v-if="index !== steps.length - 1"
                    :key="`divider-${step.title}`"
                    :color="modelValue > index ? 'white' : 'var(--blue-300)'"
                    border-style="dashed"
                    class="step-divider"
                />
            </template>
        </div>
    </div>
    <div class="mr-mobile-stepper-container mobile-only">
        <mr-divider :thickness="1" />
        <div class="mr-mobile-stepper-root">
            <template
                v-for="(step, index) in actualSteps"
                :key="`content-${step.title}`"
            >
                <div
                    class="step"
                    :class="{
                        clickable: !step.clickDisabled && index < modelValue,
                        disabled: step.clickDisabled && index < modelValue,
                    }"
                    @click.stop="click(index)"
                >
                    <mr-icon
                        v-if="step.mobileIcon?.icon"
                        :icon="step.mobileIcon.icon"
                        :color="step.mobileIcon.color"
                        size="small"
                    />
                    <mr-typography
                        v-else
                        type="span"
                        :color="step.mobileIcon?.titleColor"
                    >
                        {{ step.mobileTitle }}
                    </mr-typography>
                    <!-- change to mobile config -->
                </div>
                <mr-icon
                    v-if="index !== steps.length - 1"
                    :key="`divider-${step.title}`"
                    :icon="faChevronLeft"
                    color="var(--cool-gray-400)"
                    size="x-small"
                />
            </template>
        </div>
        <mr-divider :thickness="1" />
    </div>
</template>

<style lang="scss" scoped>
.mr-mobile-stepper-container {
    width: 100%;
    .mr-mobile-stepper-root {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 16px;
    }
}
.mr-stepper-container {
    max-width: 700px;
    margin-inline: auto;
    width: 100%;
    padding: 4px 12px 32px;
    .mr-stepper-root {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-grow: 1;

        .step-content {
            width: 60px;
            height: 36px;
            display: flex;
            flex-direction: column;
            gap: 4px;
            align-items: center;
            flex-shrink: 0;
            &.clickable {
                cursor: pointer;
            }
            &.disabled {
                cursor: not-allowed;
            }
            .icon-wrapper {
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
                flex-shrink: 0;
                width: 36px;
                height: 36px;
                &.prev {
                    background-color: white;
                }

                &.current {
                    border: 2px solid white;
                }

                &.next {
                    background-color: usePalette($p-blue, $p-300);
                }
            }
        }

        .step-divider {
            margin: 20px;
            align-self: stretch;
        }
    }
}
</style>
